import { ThemeEvents, CartUpdatedEvent } from '@theme/events';

// ============================================================
// CartDrawer — Web Component principal du tiroir panier
// Gère l'ouverture/fermeture et le re-render après update cart
// ============================================================

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.checkoutButton = null;
    this.openCartIcons = null;

    // Binding des handlers pour pouvoir les remove proprement
    this.backdropClickHandler       = this.#onBackdropClick.bind(this);
    this.checkoutButtonClickHandler = this.#handleCheckoutButtonClick.bind(this);
    this.cartUpdateHandler          = this.#handleCartUpdate.bind(this);
    this.openHandler                = this.open.bind(this);
    this.closeHandler               = this.close.bind(this);
  }

  connectedCallback() {
    this.checkoutButton = this.querySelector('[data-ref="checkout-button"]');

    // Ferme le drawer si on clique sur le backdrop
    this.addEventListener('click', this.backdropClickHandler);

    // Spinner sur le bouton checkout au clic
    this.checkoutButton.addEventListener('click', this.checkoutButtonClickHandler);

    // Écoute les updates du cart (ajout produit, changement quantité, etc.)
    document.addEventListener(ThemeEvents.cartUpdated, this.cartUpdateHandler);

    // Écoute les événements d'ouverture/fermeture globaux
    document.addEventListener(ThemeEvents.cartOpen,  this.openHandler);
    document.addEventListener(ThemeEvents.cartClose, this.closeHandler);

    // Logique spécifique à l'éditeur de thème Shopify
    if (Shopify.designMode) {
      document.addEventListener('shopify:section:unload', this.#onSectionUnload);
      if (window.theme?.isCartDrawerOpen) {
        this.open();
        window.theme.isCartDrawerOpen = false;
      }
    }
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.cartClose, this.closeHandler);
    document.removeEventListener(ThemeEvents.cartOpen,  this.openHandler);
    document.removeEventListener(ThemeEvents.cartUpdated, this.cartUpdateHandler);

    this.checkoutButton.removeEventListener('click', this.checkoutButtonClickHandler);
    this.removeEventListener('click', this.backdropClickHandler);

    if (Shopify.designMode) {
      document.removeEventListener('shopify:section:unload', this.#onSectionUnload);
    }
  }

  // Mémorise si le drawer était ouvert avant le unload (éditeur Shopify)
  #onSectionUnload = (event) => {
    if (event.detail.sectionId !== this.dataset.sectionId) return;
    window.theme = window.theme || {};
    window.theme.isCartDrawerOpen = this.isOpen();
  };

  // Ajoute le spinner sur le bouton checkout
  #handleCheckoutButtonClick(event) {
    event.target.classList.add('is-loading');
  }

  // Re-render le drawer quand le cart est mis à jour
  #handleCartUpdate(event) {
    this.renderCartContents(event.detail.data);
  }

  // Ferme si le clic est en dehors du panel (sur le backdrop)
  #onBackdropClick(event) {
    const rect = this.querySelector('.cart-drawer__inner').getBoundingClientRect();
    const isInsidePanel =
      rect.top    <= event.clientY && event.clientY <= rect.top    + rect.height &&
      rect.left   <= event.clientX && event.clientX <= rect.left   + rect.width;

    if (!isInsidePanel) this.close();
  }

  open() {
    this.classList.remove('is-closing');
    this.classList.add('is-open');
    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.add('is-closing');

    // Retire les classes après la fin de l'animation
    setTimeout(() => {
      this.classList.remove('is-open', 'is-closing');
      document.body.classList.remove('overflow-hidden');
    }, 125);
  }

  isOpen() {
    return this.classList.contains('is-open');
  }

  // Re-render l'intégralité du cart-drawer__inner avec le HTML reçu de l'API
  renderCartContents(parsedState) {
    if (!parsedState?.sections) return;
    
    const sectionId       = this.dataset.sectionId;
    const currentInner    = this.querySelector('.cart-drawer__inner');
    const newDoc          = new DOMParser().parseFromString(
      parsedState.sections[sectionId],
      'text/html'
    );

    currentInner.innerHTML = newDoc.querySelector('.cart-drawer__inner').innerHTML;

    // Met à jour la classe empty après le re-render
    setTimeout(() => {
      if (parsedState.itemCount === 0) {
        this.classList.add('cart-drawer--empty');
      } else {
        this.classList.remove('cart-drawer--empty');
      }
    });
  }
}

if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawer);
}


// ============================================================
// CartDrawerItem — Web Component pour chaque ligne du panier
// Gère la suppression et le changement de quantité par ligne
// ============================================================

class CartDrawerItem extends HTMLElement {
  constructor() {
    super();

    this.removeItemButton      = null;
    this.quantitySelectorInput = null;

    this.itemRemoveHandler          = this.#handleItemRemove.bind(this);
    this.quantityInputChangeHandler = this.#handleQuantityInputChange.bind(this);
  }

  connectedCallback() {
    this.removeItemButton      = this.querySelector('[data-ref="remove-item"]');
    this.quantitySelectorInput = this.querySelector('[data-ref="quantity-selector-input"]');

    this.removeItemButton?.addEventListener('click',  this.itemRemoveHandler);
    this.quantitySelectorInput?.addEventListener('change', this.quantityInputChangeHandler);
  }

  disconnectedCallback() {
    this.quantitySelectorInput?.removeEventListener('change', this.quantityInputChangeHandler);
    this.removeItemButton?.removeEventListener('click', this.itemRemoveHandler);
  }

  // Supprime la ligne (quantité → 0)
  #handleItemRemove(event) {
    event.preventDefault();
    this.#updateLineItemQuantity(this.dataset.index, 0);
  }

  // Met à jour la quantité de la ligne
  #handleQuantityInputChange(event) {
    event.preventDefault();
    this.#updateLineItemQuantity(this.dataset.index, this.quantitySelectorInput.value);
  }

  // Appel API Shopify cart/change.json + dispatch CartUpdatedEvent
  #updateLineItemQuantity(line, quantity) {
    this.#enableLoading();

    const body = JSON.stringify({
      line,
      quantity,
      sections:     this.closest('cart-drawer').dataset.sectionId,
      sections_url: window.location.pathname,
    });

    fetch(Theme.routes.cart_change_url, {
      method:  'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept':        'application/json',
      },
      body,
    })
      .then((response) => response.text())
      .then((responseText) => {
        const parsed = JSON.parse(responseText);

        if (parsed.errors) {
          document.dispatchEvent(new CustomEvent('toast:open', {
            detail: { type: 'error', message: parsed.errors }
          }));
          return;
        }

        // Dispatch l'événement global → CartDrawer.renderCartContents() se déclenche
        document.dispatchEvent(
          new CartUpdatedEvent({}, {
            itemCount: parsed.item_count,
            source:    'cart-drawer-item',
            sections:  parsed.sections,
          })
        );
      })
      .catch((e) => {
        console.error('CartDrawerItem error:', e);
        this.#disableLoading();
      })
      .finally(() => {
        this.#disableLoading();
      });
  }

  #enableLoading() {
    this.classList.add('cart-drawer__item--loading');
  }

  #disableLoading() {
    this.classList.remove('cart-drawer__item--loading');
  }
}

if (!customElements.get('cart-drawer-item')) {
  customElements.define('cart-drawer-item', CartDrawerItem);
}